import { useCookies } from 'react-cookie';
import mixpanel from 'mixpanel-browser';
import { useRouter } from 'next/router';
import { useRecoilState, useRecoilValue } from 'recoil';

import { COOKIE_USER_ID } from '@src/config/constants';
import { IS_PRODUCTION_ENV, MIXPANEL_TOKEN } from '@src/config/settings';
import { surveyAtom } from '@src/recoil/atoms/survey';
import { userAtom } from '@src/recoil/atoms/user';
import { selectSurveyQuestions } from '@src/recoil/selectors/surveyQuestions';
import { emailValidation } from '@src/services/surveys/utils/email';
import {
  getLocalStorageValue,
  setLocalStorageValue
} from '@src/utils/localStorage';

mixpanel.init(MIXPANEL_TOKEN, {
  debug: !IS_PRODUCTION_ENV,
  ignore_dnt: true,
  persistence: 'localStorage',
  track_pageview: false
});

/**
 * @name useSurvey
 * @description A hook for interacting with our survey(s) which makes use
 * of the Recoil state + selectors and supplies the necessary utilities/methods
 * to interact and update things
 */
export const useSurvey = () => {
  // Hooks
  const questions = useRecoilValue(selectSurveyQuestions);
  const [atom, setAtom] = useRecoilState(surveyAtom);
  const { email: userEmail, name: userName } = useRecoilValue(userAtom);
  const { query } = useRouter();
  const [cookies] = useCookies([COOKIE_USER_ID]);

  // Setup
  const { answers, error, survey } = atom;
  const { id, version } = survey;
  const uuid = cookies[COOKIE_USER_ID];
  mixpanel.identify(uuid);

  // Handlers
  const initialize = () => {
    const survey = getLocalStorageValue(id, version);
    const { data } = survey;

    // Hidden query params and prefilled answers
    questions.hidden.forEach((question) => {
      const { name, choices } = question;
      const isQueryParam = question.type === 'query_param';
      const isUUID = question.type === 'uuid';
      const isPrefillMultiple = question.type === 'prefill_multiple';
      const isPrefillSingle = question.type === 'prefill_single';

      if (isQueryParam) {
        const value = query[name] as string | undefined;
        const customMapping: Record<string, string> = {
          f: 'first_name',
          l: 'last_name'
        };
        const key = customMapping[name] ?? name;

        // Custom param mappings
        if (key === 'email_param') {
          if (emailValidation(value)) {
            data['email_param'] = value;
            data['email'] = value;
          } else {
            data['email_param'] = undefined;
            data['email'] = undefined;
          }
        } else if (value) {
          data[key] = value;
        } else {
          data[key] = undefined;
        }
      } else if (isUUID) {
        if (uuid) data[name] = uuid;
      } else if (isPrefillMultiple) {
        const values = choices?.map((choice) => choice.key) ?? [];
        if (values.length) data[name] = values;
      } else if (isPrefillSingle) {
        const values = choices?.map((choice) => choice.key) ?? [];
        if (values.length) data[name] = values[0];
      } else {
        throw new Error(`We're not ready to handle hidden "${question.type}"`);
      }
    });

    // Pre-fill some answers if user is logged in
    if (userEmail) {
      const nameArray = userName?.trim()?.split(' ') ?? [''];
      const firstName = nameArray[0] || 'First';
      const lastName = nameArray[nameArray.length - 1] || 'Last';
      data['first_name'] = firstName;
      data['last_name'] = lastName;
      data['email'] = userEmail;
    }

    const newAnswers = { ...atom.answers, ...data };

    setLocalStorageValue(id, version, newAnswers);
    setAtom({ ...atom, answers: newAnswers });
  };

  const resetSurvey = () => {
    const newAnswers = {};

    setLocalStorageValue(id, version, newAnswers);
    setAtom({ ...atom, answers: newAnswers, error: undefined });
  };

  const setAnswer = (key: string, value: unknown) => {
    setAtom((curr) => {
      setLocalStorageValue(id, version, {
        ...curr.answers,
        [key]: value
      });

      return {
        ...curr,
        answers: {
          ...curr.answers,
          [key]: value
        },
        error: undefined
      };
    });
  };

  const setError = (message?: string) => {
    setAtom({ ...atom, error: message });

    // Scroll the the bottom of the page where our error message is located
    if (message) scrollTo(0, document.body.scrollHeight);
  };

  return {
    answers,
    atom,
    error,
    initialize,
    mixpanel,
    questions,
    rawSurvey: survey,
    resetSurvey,
    setAnswer,
    setError
  };
};
